import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X, Plus, ChevronDown, ChevronUp, Building } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Lead } from '@/types/lead';
import { extractArray } from '../../lib/deliverablesResponse';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface WonLeadsAlertProps {
  onAssignDeliverables: (leadId: string) => void;
  className?: string;
}

export const WonLeadsAlert: React.FC<WonLeadsAlertProps> = ({
  onAssignDeliverables,
  className
}) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Fetch WON leads without deliverables
  const { data: wonLeadsWithoutDeliverables = [], isLoading } = useQuery<Lead[]>({
    queryKey: ['won-leads-without-deliverables'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/leads/won-without-deliverables`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        // If endpoint doesn't exist yet, return empty array
        if (response.status === 404) {
          console.warn('won-without-deliverables endpoint not found, returning empty array');
          return [];
        }
        throw new Error('Failed to fetch WON leads without deliverables');
      }
      const result = await response.json();
      // /leads/won-without-deliverables returns { data: { leads: [...], count } },
      // so a naive `result.data || result` yields an OBJECT and crashes
      // wonLeadsWithoutDeliverables.slice()/.length. extractArray pulls data.leads.
      return extractArray<Lead>(result, 'leads');
    },
    refetchInterval: 60000 // Refetch every minute
  });

  if (isDismissed || isLoading || wonLeadsWithoutDeliverables.length === 0) {
    return null;
  }

  const count = wonLeadsWithoutDeliverables.length;
  const displayedLeads = isExpanded ? wonLeadsWithoutDeliverables : wonLeadsWithoutDeliverables.slice(0, 3);

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={cn(
        'bg-amber-50 border border-amber-200 rounded-lg p-4',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-amber-800">
              {count} WON {count === 1 ? 'Lead' : 'Leads'} Without Deliverables
            </h3>
            <p className="text-sm text-amber-600">
              Assign deliverables to track commitments
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsDismissed(true)}
          className="text-amber-400 hover:text-amber-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Leads List */}
      <div className="space-y-2">
        <AnimatePresence>
          {displayedLeads.map((lead) => (
            <motion.div
              key={lead.id}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-amber-100"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Building className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate text-sm">
                    {lead.name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {lead.company}
                  </p>
                </div>
              </div>
              <button
                onClick={() => onAssignDeliverables(lead.id)}
                className="flex items-center gap-1 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-200 transition-colors flex-shrink-0"
              >
                <Plus className="w-3.5 h-3.5" />
                Assign
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Show More/Less Button */}
      {count > 3 && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-sm text-amber-700 hover:text-amber-800 mt-3 font-medium"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Show Less
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Show {count - 3} More
            </>
          )}
        </button>
      )}
    </motion.div>
  );
};
